import { OverlayModule } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  booleanAttribute,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  model,
  ModelSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormValueControl } from '@angular/forms/signals';
import { ClickOutside } from 'ngxtension/click-outside';
import { Fieldset } from '../../fieldset/fieldset.component';

import { PickCalendar } from './pick-calendar/pick-calendar.component';

@Component({
  selector: 'lepi-date-picker',
  templateUrl: './date-picker.component.html',
  imports: [Fieldset, PickCalendar, OverlayModule, ClickOutside],
})
export class DatePicker
  implements AfterViewInit, FormValueControl<Date | undefined>
{
  open = signal(false);
  confirmed = output<void>();

  yearRef = viewChild<ElementRef<HTMLInputElement>>('yearRef');
  monthRef = viewChild<ElementRef<HTMLInputElement>>('monthRef');
  dayRef = viewChild<ElementRef<HTMLInputElement>>('dayRef');

  label = input<string>('');
  isRequired = input<boolean, boolean | string>(false, {
    transform: booleanAttribute,
  });
  hideOverlay = input<boolean, boolean | string>(false, {
    transform: booleanAttribute,
  });

  value = model<Date | undefined>();

  year = model<string>('');
  year$ = toObservable(this.year);

  month = model<string>('');
  month$ = toObservable(this.month);

  day = model<string>('');
  day$ = toObservable(this.day);

  dateString = computed(() => {
    const val = this.value();
    if (!val) return '';
    const year = val.getFullYear();
    const month = String(val.getMonth() + 1).padStart(2, '0');
    const day = String(val.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });

  updateDateFromInput(ev: Event) {
    const val = (ev.target as HTMLInputElement).value;
    if (val) {
      this.value.set(new Date(val));
    } else {
      this.value.set(undefined);
    }
  }

  // year, month, day 입력값이 변경될 때만 value 업데이트 (반대 방향은 effect에서 처리)
  private updateValueFromInputs() {
    if (
      this.year()?.length === 4 &&
      this.month()?.length === 2 &&
      this.day()?.length === 2
    ) {
      const newDate = new Date(
        Number(this.year()),
        Number(this.month()) - 1,
        Number(this.day())
      );

      // 실제 값이 다를 때만 업데이트하여 무한 루프 방지
      const currentVal = this.value();
      if (!currentVal || currentVal.getTime() !== newDate.getTime()) {
        this.value.set(newDate);
      }
    }
  }

  readonly keyList = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
  readonly controlKeys = [
    'Backspace',
    'ArrowLeft',
    'ArrowRight',
    'Tab',
    'Delete',
  ];

  constructor() {
    // 외부에서 value가 변경되었을 때만 입력 필드 동기화
    effect(() => {
      const val = this.value();
      if (val) {
        // 현재 입력 필드와 값이 다를 때만 업데이트
        const currentYear = val.getFullYear().toString();
        const currentMonth = (val.getMonth() + 1).toString().padStart(2, '0');
        const currentDay = val.getDate().toString().padStart(2, '0');

        if (
          this.year() !== currentYear ||
          this.month() !== currentMonth ||
          this.day() !== currentDay
        ) {
          this.year.set(currentYear);
          this.month.set(currentMonth);
          this.day.set(currentDay);
        }
      } else if (this.year() || this.month() || this.day()) {
        this.year.set('');
        this.month.set('');
        this.day.set('');
      }
    });

    // year, month, day 변경 감지하여 value 업데이트
    effect(() => {
      this.updateValueFromInputs();
    });
  }

  handleOpen(ev: Event) {
    this.open.set(true);
    ev.stopPropagation();
    ev.preventDefault();
  }

  ngAfterViewInit(): void {
    this.year$.subscribe({
      next: year => {
        if (year?.length === 4) {
          this.monthRef()?.nativeElement.focus();
        }
      },
    });

    this.month$.subscribe({
      next: month => {
        if (month?.length === 2) {
          this.dayRef()?.nativeElement.focus();
        }
      },
    });

    this.day$.subscribe({
      next: day => {
        if (day === '33') {
          this.day.set('03');
        } else if (day === '00') {
          this.day.set('01');
        } else if (Number(day) > 31) {
          this.day.set(day.slice(1));
        }
      },
    });
  }

  handleBackspace(event: KeyboardEvent, modelSignal: ModelSignal<string>) {
    if (event.key === 'Backspace') {
      modelSignal.set('');
      event.preventDefault();
    }
  }

  handlePaste(event: ClipboardEvent): void {
    const pasteData = event.clipboardData?.getData('text') || '';
    if (!/^\d+$/.test(pasteData)) {
      event.preventDefault();
    }
  }

  blockNonNumbers(event: KeyboardEvent): void {
    if (!/^\d$/.test(event.key) && !this.controlKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  yearKeydown(event: KeyboardEvent): void {
    if (this.keyList.includes(event.key) && this.year().length === 4) {
      this.year.set('');
    }

    this.blockNonNumbers(event);
    this.handleBackspace(event, this.year);

    if (this.keyList.includes(event.key) && this.year().length < 4) {
      this.year.set(this.year() + event.key);
      event.preventDefault();
    }
  }

  monthKeydown(event: KeyboardEvent): void {
    if (this.keyList.includes(event.key) && this.month().length === 2) {
      this.month.set('');
    }

    this.blockNonNumbers(event);
    this.handleBackspace(event, this.month);

    if (this.keyList.includes(event.key)) {
      const value = this.month();
      let nextValue = value + event.key;

      if (Number(nextValue) > 12) {
        nextValue = '0' + event.key;
      }

      this.month.set(nextValue.slice(0, 2));
      event.preventDefault();
    }
  }

  dayKeydown(event: KeyboardEvent): void {
    if (this.keyList.includes(event.key) && this.day().length === 2) {
      this.day.set('');
    }

    this.blockNonNumbers(event);
    this.handleBackspace(event, this.day);

    if (this.keyList.includes(event.key)) {
      const value = this.day();
      let nextValue = value + event.key;

      if (Number(nextValue) > 31) {
        nextValue = '0' + event.key;
      }

      this.day.set(nextValue.slice(0, 2));
      event.preventDefault();
    }
  }

  reset() {
    this.year.set('');
    this.month.set('');
    this.day.set('');
    this.yearRef()?.nativeElement.focus();
  }
}
